<template>
  <main class="flex flex-col items-center w-full pt-6 text-center">
    <h1 class="text-2xl font-heading">Puzzle Number: {{ puzzleNumber }}</h1>
    <h2 class="text-xl font-semibold font-main">Time: {{ time }}s</h2>
    <h2 v-if="reducedMotion" class="text-xl font-semibold font-main">
      Reduced Motion
    </h2>
    <div
      id="container"
      ref="container"
      class="relative mt-4 overflow-hidden bg-white border border-green-700 rounded-lg select-none"
    >
      <canvas id="canvas" :width="width" :height="height" class="bg-white">
        <p>Sorry, this game doesn't work without the Canvas Enabled</p>
      </canvas>
      <canvas
        id="arrows"
        :width="width"
        :height="height"
        class="absolute top-0"
      ></canvas>
      <img
        v-if="!showFullMap"
        id="compass"
        src="@/assets/Compass.svg"
        alt="Compass"
        :style="`transform:rotate(${-lastRotation + Math.PI / 2}rad)`"
      />
    </div>
    <div class="mt-8">
      <button
        v-if="showFullMap && !startTime"
        class="button"
        @click="startGame"
      >
        Start
      </button>
      <button v-else-if="showFullMap" class="button" @click="showTerrain">
        Go
      </button>
      <button v-else-if="!finishTime" class="button" @click="showMap">
        Show Map
      </button>

      <button
        v-if="startTime && !finishTime && currentNode !== start"
        class="ml-4 button-secondary"
        @click="returnToStart"
      >
        Return to Start
      </button>
      <button
        v-else-if="startTime && finishTime"
        class="ml-8 button"
        @click="$emit('nextLeg')"
      >
        Next Puzzle
      </button>
    </div>
    <div v-if="!finishTime" class="mt-6">
      <button class="button-secondary" @click="skip">Skip</button>
    </div>
  </main>
</template>
<script>
import {
  drawTriangle,
  drawControlCircle,
  drawFlag,
  drawDot,
  drawCurve,
  setStyle,
} from '@/scripts/canvas'

export default {
  props: {
    puzzleNumber: {
      type: Number,
      default: 0,
    },
    start: {
      type: Number,
      default: 0,
    },
    finish: {
      type: Number,
      default: 1,
    },
    width: {
      type: Number,
      default: 500,
    },
    height: {
      type: Number,
      default: 500,
    },
    points: {
      type: Array,
      default: () => [],
    },
    paths: {
      type: Array,
      default: () => [],
    },
    scale: {
      type: Number,
      default: 7,
    },
  },

  data: () => ({
    canvas: undefined,
    canvasElement: undefined,

    arrows: [],

    currentNode: 0,

    animateTime: 0,
    lastPosition: false,
    lastRotation: Math.PI / 2,

    startTime: 0,
    finishTime: 0,
    time: 0,
    interval: false,
    junctions: 0,
    path: [],

    showFullMap: true,
    reducedMotion: false,
  }),

  mounted: function () {
    this.reducedMotion = this.$route.query.reducedMotion

    this.canvasElement = document.getElementById('canvas')
    this.canvas = this.canvasElement.getContext('2d')
    this.arrowCanvasElement = document.getElementById('arrows')
    this.arrowCanvas = this.arrowCanvasElement.getContext('2d')

    this.arrowCanvasElement.addEventListener('click', this.onClick)

    this.lastPosition = this.points[this.start]
    this.currentNode = this.start

    this.startTime = 0
    this.finishTime = 0
    this.time = 0
    this.interval = false
    this.junctions = 0
    this.path = []

    this.showFullMap = true
  },

  methods: {
    tick: function () {
      this.time += 1
    },

    startGame: function () {
      this.showFullMap = true
      this.startTime = Date.now() + 30000
      this.interval = setInterval(this.tick, 1000)
      this.lastPosition = this.points[this.start]
      this.currentNode = this.start
      this.showMap()
    },

    showTerrain: function () {
      this.showFullMap = false
      this.clearCanvas()
      this.fullScaleMap()
      this.rotateMap(this.lastRotation)

      this.canvas.translate(
        -this.lastPosition.x + this.width / 2,
        -this.lastPosition.y + this.height / 2
      )
      this.drawMap()
      this.drawArrows()
      this.canvas.translate(
        this.lastPosition.x - this.width / 2,
        this.lastPosition.y - this.height / 2
      )

      setStyle(this.canvas, 4, '', '#48BB78')
      drawDot(this.canvas, this.width / 2, this.height / 2, 3.5)
    },

    returnToStart: function () {
      this.currentNode = this.start
      this.arrows = []
      this.animateTime = 0
      this.lastPosition = this.points[this.start]
      this.lastRotation = Math.PI / 2
      this.showFullMap = true

      this.showMap()
    },

    showMap: function () {
      this.showFullMap = true
      this.startTime -= 30000
      if (this.time !== 0) this.time += 30

      this.clearCanvas()
      this.clearArrows()
      this.drawMap()
    },

    onClick: function (event) {
      const { offsetX: x, offsetY: y } = event

      for (const [index, angle] of this.arrows.entries()) {
        if (this.inArrowRectangle({ x, y }, angle)) {
          this.junctions += 1
          this.clearArrows()

          if (this.reducedMotion)
            this.reducedMotionMove(
              this.points[this.currentNode].connectedTo[index].to,
              this.paths[this.points[this.currentNode].connectedTo[index].id]
            )
          else
            this.startAnimation(
              this.points[this.currentNode].connectedTo[index].to,
              this.paths[this.points[this.currentNode].connectedTo[index].id]
            )
        }
      }
    },

    onFinish: function () {
      this.finishTime = Date.now()
      clearInterval(this.interval)
      this.$emit('finished', {
        choice: JSON.stringify(this.path),
        time: this.finishTime - this.startTime,
      })
    },

    skip: function () {
      clearInterval(this.interval)
      this.$emit('finished', {
        choice: false,
        time: 0,
      })
      this.$emit('nextLeg')
    },

    clearCanvas: function () {
      this.canvas.setTransform(1, 0, 0, 1, 0, 0)
      this.canvas.clearRect(
        0,
        0,
        this.canvasElement.width,
        this.canvasElement.height
      )
    },

    fullScaleMap: function () {
      this.canvas.translate(this.width / 2, this.height / 2)
      this.canvas.scale(this.scale, this.scale)
      this.canvas.translate(-this.width / 2, -this.height / 2)
    },

    rotateMap: function (angle) {
      this.canvas.translate(this.width / 2, this.height / 2)
      this.canvas.rotate(Math.PI / 2 - angle)
      this.canvas.translate(-this.width / 2, -this.height / 2)
    },

    drawMap: function () {
      const drawnPaths = []

      this.canvas.setLineDash([15, 5])
      setStyle(this.canvas, 4, 'rgba(0, 0, 0, 0.8)', this.canvas)
      this.points.map((point) =>
        point?.connectedTo?.map((path) => {
          if (!drawnPaths.includes(path.id))
            drawCurve(
              this.canvas,
              point,
              this.paths[path.id],
              this.points[path.to]
            )
          return drawnPaths.push(path.id)
        })
      )

      setStyle(this.canvas, 3.5, '', 'black')
      this.points.map((point) => drawDot(this.canvas, point.x, point.y, 5.5))

      this.drawControls()
    },

    drawFullMap: function (angle, point) {
      const { x, y } = point

      this.clearCanvas()
      this.fullScaleMap()
      this.rotateMap(angle)

      this.canvas.translate(-x + this.width / 2, -y + this.height / 2)
      this.drawMap()
      this.canvas.translate(x - this.width / 2, y - this.height / 2)

      setStyle(this.canvas, 4, '', '#48BB78')
      drawDot(this.canvas, this.width / 2, this.height / 2, 3.5)
    },

    drawControls: function () {
      if (this.showFullMap) {
        setStyle(this.canvas, 3.5, '#cc00ffbb')
        this.canvas.setLineDash([1, 0])

        drawControlCircle(
          this.canvas,
          this.points[this.finish].x,
          this.points[this.finish].y
        )

        drawTriangle(
          this.canvas,
          [this.points[this.start].x, this.points[this.start].y],
          58
        )
      } else
        drawFlag(
          this.canvas,
          this.points[this.finish].x,
          this.points[this.finish].y
        )
    },

    drawArrows: function () {
      this.arrowCanvas.setTransform(1, 0, 0, 1, 0, 0)
      this.arrowCanvas.clearRect(
        0,
        0,
        this.canvasElement.width,
        this.canvasElement.height
      )

      this.arrowCanvas.translate(this.width / 2, this.height / 2)
      this.arrowCanvas.scale(this.scale, this.scale)

      const angles = this.points[this.currentNode].connectedTo.map((path) => {
        return this.getAngleOfLine(
          this.getQuadraticBezierXYatPercent(
            this.points[this.currentNode],
            this.points[path.to],
            this.paths[path.id],
            0.1
          ),
          this.points[this.currentNode]
        )
      })

      angles.map((angle) => this.drawArrow(angle - this.lastRotation))
      this.arrows = angles.map((angle) => angle - this.lastRotation)
    },

    drawArrow: function (angle) {
      this.arrowCanvas.rotate(angle)

      this.arrowCanvas.fillStyle = '#48BB78'

      const arrow = new Path2D()
      arrow.rect(-4, -22.1, 8, 10)

      arrow.moveTo(0, -32)
      arrow.lineTo(8, -22)
      arrow.lineTo(-8, -22)
      arrow.lineTo(0, -32)

      this.arrowCanvas.fill(arrow)
      this.arrowCanvas.rotate(-angle)
    },

    clearArrows: function () {
      this.arrowCanvas.setTransform(1, 0, 0, 1, 0, 0)
      this.arrowCanvas.clearRect(
        0,
        0,
        this.canvasElement.width,
        this.canvasElement.height
      )

      this.arrows = []
    },

    startAnimation: function (to, control) {
      const start = this.points[this.currentNode]
      const end = this.points[to]

      const distanceBetweenPoints = this.quadraticBezierLength(
        start,
        control,
        end
      )
      const length = Math.round(distanceBetweenPoints * 10)

      this.animateTime = Date.now()
      this.animate({ to, start, control, end, length })
    },

    animate: function ({ to, start, control, end, length }) {
      const currentPoint = this.getQuadraticBezierXYatPercent(
        start,
        end,
        control,
        (Date.now() - this.animateTime) / length
      )
      const angle = this.getAngleOfLine(currentPoint, this.lastPosition)

      this.drawFullMap(angle, currentPoint)

      this.lastPosition = currentPoint
      this.lastRotation = angle

      if ((Date.now() - this.animateTime) / length >= 1)
        this.animationFinished(to)
      else
        requestAnimationFrame(() =>
          this.animate({ to, start, control, end, length })
        )
    },

    animationFinished: function (to) {
      this.currentNode = to
      this.animateTime = 0
      this.path.push(to)

      if (to === this.finish && !this.finishTime) this.onFinish()
      else this.drawArrows()
    },

    reducedMotionMove: function (to, control) {
      const start = this.points[this.currentNode]
      const end = this.points[to]
      const distanceBetweenPoints = this.quadraticBezierLength(
        start,
        control,
        end
      )
      const currentPoint = this.getQuadraticBezierXYatPercent(
        start,
        end,
        control,
        0.999
      )
      const angle = this.getAngleOfLine(end, currentPoint)

      this.drawFullMap(angle, end)

      this.lastPosition = end
      this.lastRotation = angle
      this.startTime -= Math.round(distanceBetweenPoints * 10) + 500
      this.time += Math.ceil(distanceBetweenPoints / 100)
      this.currentNode = to
      this.path.push(to)

      if (to === this.finish && !this.finishTime) this.onFinish()
      else this.drawArrows()
    },

    getQuadraticBezierXYatPercent: function (
      startPt,
      endPt,
      controlPt,
      percent
    ) {
      const x =
        Math.pow(1 - percent, 2) * startPt.x +
        2 * (1 - percent) * percent * controlPt.x +
        Math.pow(percent, 2) * endPt.x
      const y =
        Math.pow(1 - percent, 2) * startPt.y +
        2 * (1 - percent) * percent * controlPt.y +
        Math.pow(percent, 2) * endPt.y

      return { x, y }
    },

    getAngleOfLine: function (position, lastPosition) {
      const dx = this.lastPosition.x - position.x
      const dy = this.lastPosition.y - position.y

      return Math.atan2(dy, dx)
    },

    quadraticBezierLength: function (p0, p1, p2) {
      const ax = p0.x - 2 * p1.x + p2.x
      const ay = p0.y - 2 * p1.y + p2.y
      const bx = 2 * p1.x - 2 * p0.x
      const by = 2 * p1.y - 2 * p0.y

      const A = 4 * (ax * ax + ay * ay)
      const B = 4 * (ax * bx + ay * by)
      const C = bx * bx + by * by

      const Sabc = 2 * Math.sqrt(A + B + C)
      const A_2 = Math.sqrt(A)
      const A_32 = 2 * A * A_2
      const C_2 = 2 * Math.sqrt(C)
      const BA = B / A_2

      return (
        (A_32 * Sabc +
          A_2 * B * (Sabc - C_2) +
          (4 * C * A - B * B) * Math.log((2 * A_2 + BA + Sabc) / (BA + C_2))) /
        (4 * A_32)
      )
    },

    rotateAmount: function (start, finish, percentage) {
      const change = Math.abs((finish - start) * percentage)

      if (finish > start) return start - change
      else return start + change
    },

    rotatePoint: function (point, angle) {
      const origin = { x: this.width / 2, y: this.height / 2 }

      const x = point.x - origin.x
      const y = point.y - origin.y

      return {
        x: Math.round(x * Math.cos(angle) - y * Math.sin(angle)),
        y: Math.round(y * Math.cos(angle) + x * Math.sin(angle)),
      }
    },

    inArrowRectangle: function (point, rotation) {
      const { x, y } = this.rotatePoint(point, -rotation)

      return x <= 28 && x >= -28 && y >= -224 && y <= -84
    },
  },
}
</script>
<style>
#compass {
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  height: 5rem;
  z-index: 10;
  transition: 0.1s;
}
</style>
